import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ComplianceDashboardData,
  DocumentRequirement,
} from '../types';
import { eventService } from '../services/eventService';
import EventHelpModal from '../components/EventHelpModal';

/**
 * Compliance Dashboard — displays NoR-based compliance status with progress tracking.
 *
 * Requirements:
 *  10.1 — Display race summary header (name, date, distance, category)
 *  10.2 — Extract race details from NoR
 *  10.3 — Fall back to race record when NoR not available
 *  10.4 — Show source indicator (from NoR vs from race record)
 *  11.4 — Display document requirements list
 *  11.5 — Group requirements by category
 *  12.1 — Show progress as "X of Y" for each requirement
 *  12.2 — Show checkmark for complete requirements
 *  12.3 — Show cross for missing requirements
 *  12.4 — Show partial indicator for in-progress requirements
 *  15.3 — Show expiry warnings for documents expiring soon
 *  15.4 — Show which crew member's document satisfies each requirement
 */

/** Get status class for requirement progress. */
function getProgressStatusClass(status: string): string {
  switch (status) {
    case 'complete': return 'status-complete';
    case 'partial': return 'status-partial';
    case 'missing': return 'status-missing';
    default: return 'status-unknown';
  }
}

/** Get status icon for requirement. */
function getProgressIcon(status: string): string {
  switch (status) {
    case 'complete': return '✓';
    case 'partial': return '◐';
    case 'missing': return '✗';
    default: return '?';
  }
}

/** Format document type for display (snake_case to Title Case). */
function formatDocumentType(docType: string): string {
  return docType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Race summary header component. */
const RaceSummaryHeader: React.FC<{
  summary: ComplianceDashboardData['raceSummary'];
}> = ({ summary }) => (
  <div className="race-summary-header" data-testid="race-summary">
    <h1 className="race-title">{summary.name}</h1>
    <div className="race-info-cards">
      <div className="info-card">
        <span className="info-label">Category</span>
        <span className="info-value category-value">{summary.category}</span>
      </div>
      {summary.date && (
        <div className="info-card">
          <span className="info-label">Date</span>
          <span className="info-value">{summary.date}</span>
        </div>
      )}
      {summary.distance && (
        <div className="info-card">
          <span className="info-label">Distance</span>
          <span className="info-value">{summary.distance}</span>
        </div>
      )}
    </div>
    <div className="source-indicator" data-testid="source-indicator">
      <span className={`source-badge ${summary.source}`}>
        {summary.source === 'nor' ? 'From Notice of Race' : 'From Race Record'}
      </span>
    </div>
  </div>
);

/** Overall progress summary component. */
const ProgressSummary: React.FC<{
  progress: ComplianceDashboardData['overallProgress'];
}> = ({ progress }) => {
  const isComplete = progress.completedRequirements === progress.totalRequirements;
  
  return (
    <div className={`progress-summary ${isComplete ? 'complete' : 'incomplete'}`} data-testid="progress-summary">
      <div className="progress-icon">
        {isComplete ? '✓' : '⚠'}
      </div>
      <div className="progress-content">
        <h2>{isComplete ? 'All Requirements Met' : 'Requirements In Progress'}</h2>
        <div className="progress-bar-container">
          <div 
            className="progress-bar" 
            style={{ width: `${progress.percentComplete}%` }}
          />
        </div>
        <p>
          {progress.completedRequirements} of {progress.totalRequirements} requirements complete
          ({progress.percentComplete.toFixed(0)}%)
        </p>
      </div>
    </div>
  );
};

/** Requirements table component. */
const RequirementsTable: React.FC<{
  requirements: DocumentRequirement[];
  title: string;
}> = ({ requirements, title }) => {
  if (requirements.length === 0) return null;

  return (
    <section className="requirements-section">
      <h3>{title} ({requirements.length})</h3>
      <div className="table-responsive">
        <table className="requirements-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Document Type</th>
              <th>Description</th>
              <th>Progress</th>
              <th>Linked Documents</th>
            </tr>
          </thead>
          <tbody>
            {requirements.map(req => (
              <tr key={req.requirementId} className={getProgressStatusClass(req.status)}>
                <td className="status-cell">
                  <span className={`status-icon ${getProgressStatusClass(req.status)}`}>
                    {getProgressIcon(req.status)}
                  </span>
                </td>
                <td className="doc-type-cell">{formatDocumentType(req.documentType)}</td>
                <td className="description-cell">{req.description}</td>
                <td className="progress-cell">
                  {req.currentCount} / {req.requiredQuantity}
                </td>
                <td className="linked-docs-cell">
                  {req.linkedDocuments.length > 0 ? (
                    <ul className="linked-docs-list">
                      {req.linkedDocuments.map(doc => (
                        <li key={doc.documentId}>
                          {doc.crewName && <span className="crew-name">{doc.crewName}</span>}
                          <span className={`verification-badge ${doc.verificationStatus}`}>
                            {doc.verificationStatus}
                          </span>
                          {doc.isExpiringSoon && (
                            <span className="expiry-warning" title={`Expires: ${doc.expiryDate}`}>
                              ⚠
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <span className="no-docs">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const ComplianceDashboard: React.FC = () => {
  const { raceId } = useParams<{ raceId: string }>();
  const navigate = useNavigate();

  const [dashboardData, setDashboardData] = useState<ComplianceDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAskModal, setShowAskModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!raceId) return;

    setLoading(true);
    setError(null);
    try {
      const [dashboard] = await Promise.all([
        eventService.getComplianceDashboard(raceId),
        eventService.getEventCrew(raceId)
      ]);

      setDashboardData(dashboard);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError('Failed to load compliance dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [raceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleBack = () => {
    navigate('/events');
  };

  const handleBuildCrew = () => {
    navigate(`/events/${raceId}/crew`);
  };

  const handleGoToDocuments = () => {
    navigate('/documents');
  };

  if (loading) {
    return (
      <div className="compliance-dashboard">
        <div className="loading-state" data-testid="dashboard-loading">
          <div className="loading-spinner"></div>
          <p>Loading compliance dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="compliance-dashboard">
        <div className="error-state" data-testid="dashboard-error">
          <h2>Unable to Load Dashboard</h2>
          <p>{error || 'Could not load compliance data for this event.'}</p>
          <div className="error-actions">
            <button onClick={fetchData} className="btn-retry">Retry</button>
            <button onClick={handleBack} className="btn-back">Back to Events</button>
          </div>
        </div>
      </div>
    );
  }

  // Group requirements by category
  const crewRequirements = dashboardData.requirements.filter(r => r.category === 'crew');
  const boatRequirements = dashboardData.requirements.filter(r => r.category === 'boat');

  return (
    <div className="compliance-dashboard">
      <div className="page-header">
        <button onClick={handleBack} className="btn-back" data-testid="back-btn">
          ← Back to Events
        </button>
        <button 
          onClick={() => setShowAskModal(true)} 
          className="btn-ask"
          data-testid="ask-btn"
        >
          Ask ❓
        </button>
      </div>

      <RaceSummaryHeader summary={dashboardData.raceSummary} />
      
      <ProgressSummary progress={dashboardData.overallProgress} />

      {error && (
        <div className="error-banner" data-testid="dashboard-error-banner">
          <p>{error}</p>
          <button onClick={fetchData} className="btn-retry">Retry</button>
        </div>
      )}

      <div className="dashboard-content">
        {/* Crew Requirements Table */}
        <RequirementsTable 
          requirements={crewRequirements} 
          title="Crew Document Requirements" 
        />

        {/* Boat Requirements Table */}
        <RequirementsTable 
          requirements={boatRequirements} 
          title="Boat Document Requirements" 
        />

        {/* No Requirements State */}
        {dashboardData.requirements.length === 0 && (
          <div className="empty-state" data-testid="no-requirements">
            <p>No document requirements found for this event.</p>
            <p>Upload a Notice of Race to extract requirements automatically.</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="dashboard-actions">
          <button onClick={handleBuildCrew} className="btn-primary">
            Build Crew
          </button>
          <button onClick={handleGoToDocuments} className="btn-secondary">
            Upload Documents
          </button>
          <button onClick={fetchData} className="btn-secondary">
            Refresh Status
          </button>
        </div>
      </div>

      {/* Ask Modal */}
      {raceId && dashboardData && (
        <EventHelpModal
          isOpen={showAskModal}
          onClose={() => setShowAskModal(false)}
          raceId={raceId}
          eventName={dashboardData.raceSummary.name}
        />
      )}
    </div>
  );
};

export default ComplianceDashboard;
